import React, {useEffect, useState} from "react";
import { Box, useTheme, useMediaQuery } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { useGetAllDealsQuery } from "../../state/api";
import Header from "components/Header";
import FlexBetween from "components/FlexBetween";
import DataGridCustomToolbar from "components/DataGridCustomToolbar";

const useDebounce = (value, delay) => {
    const [debouncedValue, setDebouncedValue] = useState(value);

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedValue(value);
        }, delay);

        return () => {
            clearTimeout(handler);
        };
    }, [value, delay]);

    return debouncedValue;
};

const Deals = () => {
    const theme = useTheme();
    const isNonMediumScreens = useMediaQuery("(min-width: 1200px)");
    const { data: dealsData, isLoading } = useGetAllDealsQuery();
    const deals = dealsData?.deals || [];
    const total = dealsData?.total || 0;
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [searchInput, setSearchInput] = useState("");
    const debouncedSearchInput = useDebounce(searchInput, 500);

    const filteredDeals = deals.filter(deal =>
        deal.title.toLowerCase().includes(debouncedSearchInput.toLowerCase())
    );

    const formatDate = (timestamp) => {
        if (!timestamp) return '';

        const date = new Date(timestamp * 1000);
        return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: '2-digit',
        });
    };

    const columns = [
        {
        field: "id",
        headerName: "ID",
        minWidth: 50,
        flex: isMobile ? 0.2 : 0.3,
        },
        {
        field: "title",
        headerName: "Event",
        minWidth: 180,
        flex: isMobile ? 1 : 1.2,
        },
        {
        field: "primaryContact.name",
        headerName: "Contact",
        minWidth: 120,
        flex: isMobile ? 0 : 0.7,
        valueGetter: (params) => params.row.primaryContact?.name || '',
        },
        {
        field: "primaryCompany.name",
        headerName: "Company",
        minWidth: 110,
        flex: isMobile ? 0 : 0.7,
        valueGetter: (params) => params.row.primaryCompany?.name || '',
        },
        {
        field: "stage",
        headerName: "Stage",
        minWidth: 150,
        flex: isMobile ? 0 : 0.8,
        },
        {
        field: "dealValue",
        headerName: "Value",
        minWidth: 80,
        flex: isMobile ? 0 : 0.5,
        valueFormatter: (params) => {
            const valueFormatted = Number.parseFloat(params.value).toFixed(2);
            return `${valueFormatted} €`;
        }
        }, 
        {
        field: "closedDate",
        headerName: "Closed Date",
        minWidth: 80,
        flex: isMobile ? 0 : 0.5,
        valueFormatter: (params) => formatDate(params.value),
        },
        {
        field: "status",
        headerName: "Status",
        minWidth: 50,
        flex: isMobile ? 0 : 0.5,
        },
    ];

    return (
        <Box m="1.5rem 2.5rem">
        <FlexBetween>
            <Header title="Deals" subtitle="Salesmate Deals" />
            <Box>
            </Box>
        </FlexBetween>

        <Box
            mt="20px"
            display="grid"
            gridTemplateColumns="repeat(12, 1fr)"
            gridAutoRows="160px"
            gap="20px"   
            sx={{
            "& > div": { gridColumn: isNonMediumScreens ? undefined : "span 12" },
            }}
        >
            <Box
            height={750} 
            gridColumn="span 12"
            gridRow="span 3"
            sx={{
                "& .MuiDataGrid-root": {
                border: "none",
                borderRadius: "5rem",
                },
                "& .MuiDataGrid-cell": {
                borderBottom: "none",
                width: '100%',
                },
                "& .MuiDataGrid-columnHeaders": {
                backgroundColor: theme.palette.background.alt,
                color: theme.palette.secondary[300],
                borderBottom: "none",
                },
                "& .MuiDataGrid-virtualScroller": {
                backgroundColor: theme.palette.background.alt,
                },
                "& .MuiDataGrid-footerContainer": {
                backgroundColor: theme.palette.background.alt,
                color: theme.palette.secondary[100],
                borderTop: "none",
                },
                "& .MuiDataGrid-toolbarContainer .MuiButton-text": {
                color: `${theme.palette.secondary[200]} !important`,
                },
            }}
            >
            <DataGrid
                loading={isLoading || !dealsData}
                getRowId={(deal) => deal.id}
                rows={filteredDeals}
                columns={columns}
                rowCount={total}
                components={{
                    Toolbar: () => (
                        <DataGridCustomToolbar
                        setSearchInput={setSearchInput}
                        />
                    ) 
                }}
            />
            
            </Box>
        </Box>
        </Box>
    );
};

export default Deals;